import { fetch } from '@maxtropy/components';
import { Key } from 'react';
import { AlarmPushStatus, Operator, AlarmLevel, ChannelAppIDType } from '@/shared/types';
import { PageRequest, PageResponse } from './page';

export interface AlrmPushPage {
  id: number;
  serialNumber: string;
  name: string;
  status: AlarmPushStatus;
  rootMcid: string;
  tenantName: string;
  ouName: string;
  pushUserNum?: number;
  linkDeviceNum?: number;
  lastUpdateTime: string;
  lastUpdateUserName: string;
  lastUpdateSource: Operator;
  ouAdmin: boolean;
}

export interface AlarmPushPageRequest extends PageRequest {
  name?: string;
  status?: AlarmPushStatus;
  ouId?: string;
}

export function getAlrmPushPage(params: AlarmPushPageRequest): Promise<PageResponse<AlrmPushPage>> {
  return fetch(
    `/api/v2/device-alarm-push-strategy/page`,
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}

export function changeAlrmStatusOrDelete(pushId: number, status: AlarmPushStatus): Promise<boolean> {
  return fetch(
    `/api/v2/device-alarm-push-strategy/update-status`,
    {
      method: 'POST',
      body: JSON.stringify({
        pushId,
        status,
      }),
    },
    true
  );
}

export interface PushCenterStaffLinkRequestList {
  staffId: number;
  thirdPartyIdList: ChannelAppIDType[];
  resumeAlarmIdList: ChannelAppIDType[];
}

export interface OutsiderRequest {
  contactPhone: string;
  contactName: string;
  email?: string;
  thirdPartyIdList: ChannelAppIDType[];
  resumeAlarmIdList: ChannelAppIDType[];
}

export interface OutsiderResponse extends OutsiderRequest {
  id: Key;
}

export interface CreateAlarmPushRequest {
  name: string;
  ouId: number;
  levels: AlarmLevel[];
  pushMethod?: string;
  pushConfig?: string;
  pushCenterStaffLinkRequestList: PushCenterStaffLinkRequestList[];
  pushCenterRuleLinkRequestList: any[];
  deviceIdList: number[];
  outsidersList: OutsiderRequest[];
}

export function createAlarmPush(params: CreateAlarmPushRequest): Promise<boolean> {
  return fetch(
    `/api/v2/device-alarm-push-strategy/insertV2`,
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}

export interface UpdateAlarmPushRequest {
  id: number;
  name: string;
  pushMethod?: string;
  pushConfig?: string;
  ouId?: number;
  levels?: AlarmLevel[];
  oldPushCenterRuleLinkRequestList?: any[];
  newPushCenterRuleLinkRequestList?: any[];
  oldPushCenterStaffLinkRequestList: PushCenterStaffLinkRequestList[];
  newPushCenterStaffLinkRequestList: PushCenterStaffLinkRequestList[];
  addDeviceIdList: number[];
  deleteDeviceIdList: number[];
  oldOutsidersIds: Key[];
  newOutsiders: OutsiderRequest[];
}

export function updateAlarmPush(params: UpdateAlarmPushRequest): Promise<boolean> {
  return fetch(
    `/api/v2/device-alarm-push-strategy/updateV2`,
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}

export interface PushCenterStaffLinkVoList extends AlrmPushStaffPageResponse {
  thirdPartyAppId: ChannelAppIDType[];
  resumeAlarmIdList: ChannelAppIDType[];
}

export interface AlarmPush {
  id: number;
  name: string;
  tenantName: string;
  rootMcid: string;
  ouId: number;
  ouName: string;
  levels: AlarmLevel[];
  pushCenterStaffLinkVoList: PushCenterStaffLinkVoList[];
  pushCenterRuleLinkVoList?: any[];
  deviceAlarmPushDeviceVoList: AlrmPushDevice[];
  outsiders: OutsiderResponse[];
  pushMethod?: string;
  pushConfig?: string;
}

export function getAlarmPush(id: string): Promise<AlarmPush> {
  return fetch(
    `/api/v2/device-alarm-push-strategy/details`,
    {
      method: 'POST',
      body: JSON.stringify({ id }),
    },
    true
  );
}

export interface AlarmPushDevicePageRequest extends PageRequest {
  ouId: string;
  name?: string;
  deviceTypes?: Array<number> | undefined;
}

export interface AlrmPushDevice {
  deviceId: number;
  code: string;
  name: string;
  deviceType: string;
  customerName: string;
}

export function getAlarmPushDevicePage(params: AlarmPushDevicePageRequest): Promise<PageResponse<AlrmPushDevice>> {
  return fetch(
    `/api/v2/device-alarm-push-strategy/page-device`,
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}

export interface AlarmPushStaffPageRequest extends PageRequest {
  ouId?: string;
  name?: string;
  humanCode?: string;
  staffCode?: string;
  phone?: string;
}

export interface AlrmPushStaffPageResponse {
  staffId: number;
  humanCode: string;
  name: string;
  phone: string;
  email?: string;
  staffCode: string;
  customerName: string;
}

export function getAlarmPushStaffPage(
  params: AlarmPushStaffPageRequest
): Promise<PageResponse<AlrmPushStaffPageResponse>> {
  return fetch(
    `/api/v2/device-alarm-push-strategy/page-staff`,
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}
